<template>
  <modal
    :value="isModalOpen"
    title="Create Client Profile"
    :isLoading="isLoading"
    @click:outside="closeDialog"
  >
    <template #buttons>
      <qvoButton text @click="clickSecondaryButton">{{
        step === 2 ? "Back" : "Cancel"
      }}</qvoButton>
      <qvoButton @click="clickPrimaryButton" :disabled="!isFormValid">
        {{ step === 2 ? "Create" : "Next" }}
      </qvoButton>
    </template>
    <h6 v-if="step === 1">Basic Client Info</h6>
    <h6 v-if="step === 2">Client Address</h6>
    <v-form ref="form" class="fields" v-model="isFormValid">
      <div
        class="field-row"
        v-for="(row, key) in layout.modalSteps[step]"
        :key="key"
      >
        <div
          class="field"
          v-for="field in row"
          :key="getFieldDataValue({ id: field.id, value: 'FieldId' })"
          :style="`grid-column: ${field.column || 1} / span ${
            field.columnWidth || 4
          }`"
        >
          <string
            v-if="field.specialField === 'ClientId'"
            id="Client ID"
            :label="
              getFieldDataValue({
                id: 'ClientId',
                category: 'Info',
                value: 'Label',
              }) || 'Client ID'
            "
            :placeholder="
              getFieldDataValue({
                id: 'ConfigLabel',
                category: 'Info',
                value: 'Placeholder',
              }) ||
              getFieldDataValue({
                id: 'ClientId',
                category: 'Info',
                value: 'Label',
              }) ||
              'Client ID'
            "
            :value="clientId"
            @setConfigValue="setClientId"
            class="special-field"
            :rules="clientIdRegex"
          />
          <string
            v-if="field.specialField === 'ConfigLabel'"
            id="Config Label"
            category="Info"
            :label="
              getFieldDataValue({
                id: 'ConfigLabel',
                category: 'Info',
                value: 'Label',
              })
            "
            :placeholder="
              getFieldDataValue({
                id: 'ConfigLabel',
                category: 'Info',
                value: 'Placeholder',
              }) ||
              getFieldDataValue({
                id: 'ConfigLabel',
                category: 'Info',
                value: 'Label',
              })
            "
            :value="getConfigValue({ id: 'ConfigLabel', category: 'Info' })"
            @setConfigValue="
              (value) =>
                setConfigValue({
                  category: 'Info',
                  fieldId: 'ConfigLabel',
                  value,
                })
            "
            class="special-field"
            :rules="[{ Type: 'required' }]"
          />
          <configField
            v-else
            id="New"
            category="ClientInformation"
            :field="getFieldData({ id: field.id })"
            :value="getConfigValue({ id: field.id })"
            @setConfigValue="setConfigValue"
            isInModal
          />
        </div>
      </div>
    </v-form>
  </modal>
</template>
  
  <script>
import { mapGetters } from "vuex";
import getFieldData from "@/util/getFieldData";

import qvoButton from "@/qux-common/qux22/components/molecules/button";
import modal from "@/qux-common/qux22/components/molecules/modal";

import configField from "@/components/config-field";
import string from "@/components/config-field/string";

// Defining the UI layout.  Each step of the modal will have an array of rows for the UI.
// Each row will be an array of objects describing the fields in that row and column positioning and width.
const layout = {
  modalSteps: {
    1: [
      [{ specialField: "ClientId", columnWidth: 2 }],
      [{ specialField: "ConfigLabel", columnWidth: 2 }],
      [
        { id: "LegalName", columnWidth: 2 },
        { id: "CommonName", column: 3, columnWidth: 2 },
      ],
      [{ id: "PhoneNumber", columnWidth: 2 }],
      [{ id: "FaxNumber", columnWidth: 2 }],
      [{ id: "EmailAddress" }],
      [{ id: "URL" }],
    ],
    2: [
      [{ id: "AddressLine1" }],
      [{ id: "AddressLine2" }],
      [{ id: "AddressLine3" }],
      [
        { id: "City", columnWidth: 2 },
        { id: "State", column: 3, columnWidth: 1 },
        { id: "Zip", column: 4, columnWidth: 1 },
      ],
      [{ id: "Country", columnWidth: 2 }],
    ],
  },
};

export default {
  components: {
    configField,
    qvoButton,
    modal,
    string,
  },
  data() {
    return {
      clientId: "",
      configField: "",
      dialog: false,
      layout,
      step: 1,
      isFormValid: false,
    };
  },
  computed: {
    isLoading() {
      return (
        this.pendingCalls.includes("/client/list") ||
        this.pendingCalls.includes("/client/create") ||
        this.pendingCalls.includes("/QFDConfig/v1/save")
      );
    },
    clientIdRegex() {
      return [
        { Type: "required" },
        { Type: "unique", ExistingValues: this.existingIds },
      ];
    },
    ...mapGetters({
      updatedValues: "getUpdatedConfigValues",
      configTemplate: "getConfigTemplate",
      pendingCalls: "getPendingCallList",
      isModalOpen: "getCreateClientModalOpen",
      existingIds: "getExistingIds",
    }),
  },
  methods: {
    fieldList(category = "ClientInformation") {
      const fieldData = this.configTemplate && this.configTemplate[category];
      return fieldData && fieldData.AvailableFieldList;
    },
    getFieldData({ id, category }) {
      return getFieldData(this.fieldList(category), id);
    },
    getFieldDataValue({ id, category, value }) {
      const fieldData = this.getFieldData({ id, category });
      return fieldData && fieldData[value];
    },
    setConfigValue({ category, fieldId, value }) {
      this.$store.dispatch("setConfigValue", {
        ClientInfoRuleId: "New",
        category,
        fieldId,
        value,
      });
    },
    getConfigValue({ id, category = "ClientInformation" }) {
      return (
        this.updatedValues["New"] &&
        this.updatedValues["New"][category] &&
        this.updatedValues["New"][category][id]
      );
    },
    setClientId(value) {
      this.clientId = value;
    },
    setConfigLabel(value) {
      this.configLabel = value.replace(/\s/g, "");
    },
    clearConfigValues() {
      this.$store.dispatch("clearNewConfigValues");
    },
    createConfig() {
      this.$store
        .dispatch("saveConfig", {
          ClientId: this.clientId,
          ClientInfoRuleId: "New",
        })
        .then(() => {
          // close dialog only after successful save
          this.$router.push("/clients");
          this.closeDialog();
        });
    },
    closeDialog() {
      this.$store.dispatch("closeCreateClientModal");
    },
    clickPrimaryButton() {
      if (this.step < 2) {
        // There are only two steps, so this may be overkill, but makes it easier if we add extra steps, can just bump this higher
        this.step = this.step + 1;
      } else {
        this.createConfig();
      }
    },
    clickSecondaryButton() {
      if (this.step > 1) {
        // There are only two steps, so this may be overkill, but makes it easier if we add extra steps
        this.step = this.step - 1;
      } else {
        this.closeDialog();
      }
    },
  },
  watch: {
    isModalOpen() {
      if (this.isModalOpen) {
        this.$store.dispatch("fetchExistingIds");
      } else {
        this.clearConfigValues();
        this.$refs.form.reset();
        this.step = 1;
        this.clientId = "";
        this.configLabel = "";
      }
    },
  },
};
</script>
  
  <style lang="scss" scoped>
.fields {
  display: grid;
}
.field-row {
  display: grid;
  grid-template-columns: 2fr 2fr 1.5fr 1.5fr;
  gap: $qvo-spacing-16;
}
.special-field {
  margin-bottom: $qvo-spacing-24;
}
h6 {
  margin-bottom: $qvo-spacing-8;
}
</style>